import { Link } from 'react-router-dom';
import { Shield, BookOpen, Award, Heart } from 'lucide-react';
import { SEO } from '../components/SEO';

const EXPERTS = [
  {
    name: 'Isabella Chen',
    role: 'Founder & Lead Authenticator',
    cred: 'GIA Graduate Gemologist',
    detail: '12+ years · Van Cleef · Cartier · Bulgari',
    image:
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'James Thornton',
    role: 'Senior Editor & Market Analyst',
    cred: 'Luxury Market Expert',
    detail: '8+ years · Market Trends · Investment',
    image:
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Natalie Brooks',
    role: 'Authentication Specialist',
    cred: 'GIA Pearls Graduate',
    detail: '10+ years · Pearls · Vintage',
    image:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'David Martinez',
    role: 'Contributing Writer',
    cred: 'Jewelry Collector',
    detail: '6+ years · Buying Guides · Estate',
    image:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Sophie Laurent',
    role: 'Brand Specialist & Editor-at-Large',
    cred: 'Former Boutique Brand Specialist',
    detail: '7+ years · Chopard · Piaget',
    image:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Michael Chen',
    role: 'Diamond Specialist',
    cred: 'GIA Graduate Diamonds',
    detail: '15+ years · Harry Winston · Graff',
    image:
      'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Elena Rossi',
    role: 'Vintage Jewelry Historian',
    cred: 'Art History PhD',
    detail: '12+ years · Art Deco · Victorian',
    image:
      'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Robert Kim',
    role: 'Watch & Jewelry Appraiser',
    cred: 'ASA Certified',
    detail: '18+ years · Appraisals · Estate',
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Amanda Foster',
    role: 'Styling & Trends Editor',
    cred: 'Former Vogue Contributor',
    detail: '8+ years · Styling · Trends',
    image:
      'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600&h=600&fit=crop&crop=faces&q=85',
  },
  {
    name: 'Thomas Wright',
    role: 'Platform Review Specialist',
    cred: 'E-Commerce Expert',
    detail: '5+ years · Fashionphile · RealReal',
    image:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=600&fit=crop&crop=faces&q=85',
  },
];

const VALUES = [
  {
    icon: Shield,
    title: 'Trust First',
    body: 'We only recommend platforms and practices we would use ourselves. Your confidence matters more than any commission.',
  },
  {
    icon: BookOpen,
    title: 'Education',
    body: 'Knowledge empowers better decisions. Our guides help you understand what you are buying and why it matters.',
  },
  {
    icon: Award,
    title: 'Excellence',
    body: "We cover the world's finest jewelry houses because quality matters. Only the best deserves your investment.",
  },
  {
    icon: Heart,
    title: 'Passion',
    body: 'We genuinely love fine jewelry and the stories behind each piece. That passion drives everything we create.',
  },
];

const PROCESS = [
  ['01', 'Primary Research', 'We examine actual pieces, visit boutiques, and test platforms ourselves before writing about them.'],
  ['02', 'Expert Review', 'All authentication guides are reviewed by our GIA-certified gemologists for accuracy.'],
  ['03', 'Market Verification', 'Pricing data is tracked across multiple platforms and updated regularly to ensure accuracy.'],
  ['04', 'Ongoing Updates', 'We revisit and update our content as markets change and new information becomes available.'],
];

const STATS = [
  ['15+', 'Years of Experience'],
  ['5,000+', 'Pieces Authenticated'],
  ['$12M+', 'Jewelry Evaluated'],
  ['50K+', 'Readers Monthly'],
];

export const AboutPage = () => {
  return (
    <div data-testid="about-page" className="bg-[#FAF9F6]">
      <SEO
        title="About Us — Our Experts & Editorial Mission"
        description="Meet the GIA-certified gemologists and luxury jewelry specialists behind Jewelry Refined. 15+ years of authentication, valuation, and pre-owned market expertise."
        url="/about"
      />

      {/* Editorial Hero */}
      <section className="border-b border-[#E5E0D8]">
        <div className="container-editorial py-20 lg:py-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-[#888] mb-12">
            <Link to="/" className="hover:text-[#111] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#111]">About</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="overline overline-gold mb-6 animate-fade-in">About Jewelry Refined</p>
              <h1 className="heading-editorial heading-editorial-lg text-[#111] mb-8 animate-fade-in-up">
                A Trusted Voice <br />
                in <span className="italic">Pre-Owned</span> Luxury
              </h1>
              <p className="text-[#555] text-xl leading-relaxed font-light mb-6 max-w-xl">
                Founded by certified gemologists and luxury specialists with{' '}
                <span className="text-[#111]">15+ years of combined experience</span> in
                authentication, valuation, and the secondary market.
              </p>
              <p className="text-[#555] leading-relaxed max-w-xl">
                We've personally authenticated thousands of pieces from Van Cleef & Arpels,
                Cartier, Tiffany & Co., and other prestigious houses. Our mission is to share
                that expertise so you can shop the secondary market with confidence.
              </p>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] overflow-hidden img-zoom">
                <img
                  src="https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?w=1000"
                  alt="Authenticator examining luxury jewelry with a loupe"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 lg:-left-12 bg-[#111] text-white p-6 lg:p-8 max-w-[260px]">
                <p className="overline text-[#C69C6D] mb-2">Verified Expertise</p>
                <p className="font-heading text-lg leading-snug">
                  GIA-trained gemologists with auction-house experience
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-[#111] text-white">
        <div className="container-editorial py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
            {STATS.map(([num, label], i) => (
              <div key={i} className={`text-center px-4 ${i === 0 ? 'lg:pl-0' : ''}`}>
                <p className="font-heading font-light text-4xl lg:text-6xl text-white">{num}</p>
                <p className="overline text-white/60 mt-3">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet The Experts — Editorial Grid */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Team</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">Meet Our Experts</h2>
            </div>
            <p className="hidden md:block text-sm text-[#555] max-w-xs">
              Ten specialists with decades of hands-on experience across authentication,
              valuation, and the secondary market.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-12">
            {EXPERTS.map((expert) => (
              <article key={expert.name} className="group" data-testid={`expert-${expert.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="aspect-[3/4] overflow-hidden img-zoom mb-5">
                  <img
                    src={expert.image}
                    alt={expert.name}
                    className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
                  />
                </div>
                <p className="overline overline-gold mb-2">{expert.cred}</p>
                <h3 className="font-heading text-xl text-[#111] mb-1">{expert.name}</h3>
                <p className="text-sm text-[#555]">{expert.role}</p>
                <p className="text-xs text-[#888] mt-2">{expert.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Process */}
      <section className="section-editorial border-t border-[#E5E0D8] bg-white/40">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="overline overline-gold mb-3">Methodology</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl mb-6">
                Our Editorial <br />
                <span className="italic">Process</span>
              </h2>
              <p className="text-[#555] leading-relaxed">
                Every piece of content is researched, written, and reviewed by our team of
                certified experts. We don't publish anything we haven't personally verified.
              </p>
            </div>

            <div className="lg:col-span-8 lg:border-l border-[#E5E0D8] lg:pl-12">
              <div className="space-y-12">
                {PROCESS.map(([num, title, body]) => (
                  <div key={num} className="flex gap-6 lg:gap-10">
                    <p className="font-heading text-3xl lg:text-4xl text-[#C69C6D] font-light">{num}</p>
                    <div className="flex-1 pt-1">
                      <h3 className="font-heading text-2xl text-[#111] mb-2">{title}</h3>
                      <p className="text-[#555] leading-relaxed">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-editorial border-t border-[#E5E0D8]">
        <div className="container-editorial">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="overline overline-gold mb-3">Our Values</p>
            <h2 className="heading-editorial text-4xl lg:text-5xl text-[#111]">
              What <span className="italic">Guides</span> Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E0D8]">
            {VALUES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-[#FAF9F6] p-10 lg:p-12">
                <Icon className="w-8 h-8 text-[#C69C6D] mb-6" strokeWidth={1.2} />
                <h3 className="font-heading text-xl text-[#111] mb-4">{title}</h3>
                <p className="text-sm text-[#555] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Press Strip */}
      <section className="border-t border-[#E5E0D8] py-16 bg-white/40">
        <div className="container-editorial text-center">
          <p className="overline mb-8">Our Work Has Been Referenced By</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {['JCK Magazine', 'National Jeweler', 'The Knot', 'Brides', 'Town & Country'].map((name) => (
              <span key={name} className="font-heading text-lg lg:text-xl text-[#555] tracking-wide">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final Editorial CTA */}
      <section className="bg-[#111] text-white py-20 lg:py-28">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="overline text-[#C69C6D] mb-6">Get In Touch</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl text-white mb-6">
                Have an authentication <br />
                <span className="italic">question?</span>
              </h2>
            </div>
            <div className="lg:text-right">
              <p className="text-white/60 leading-relaxed mb-8 max-w-md lg:ml-auto">
                Our team is happy to help with authentication questions, buying advice, or
                general inquiries about pre-owned luxury jewelry.
              </p>
              <div className="flex flex-wrap gap-4 lg:justify-end">
                <Link to="/contact" className="btn-editorial border-white text-white hover:bg-white hover:text-[#111]">
                  Contact The Team
                </Link>
                <Link to="/guides/authenticate-luxury-jewelry" className="btn-editorial border-[#C69C6D] text-[#C69C6D] hover:bg-[#C69C6D] hover:text-white">
                  Read Our Guides
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-white/10 text-center">
            <p className="text-sm text-white/50 max-w-2xl mx-auto">
              Jewelry Refined earns commissions through affiliate partnerships with the
              platforms we recommend.{' '}
              <Link to="/affiliate-disclosure" className="text-[#C69C6D] hover:text-white underline underline-offset-4 transition-colors">
                Read our full affiliate disclosure
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
